import type { 
  User, Project, Milestone, Task, Attachment, MindMapNode, 
  CalendarSubscription, Stats, LoginForm, RegisterForm, ApiResponse 
} from '../types';

const API_BASE = '/api';

// 获取token
function getToken(): string | null {
  return localStorage.getItem('token');
}

// 通用请求函数
async function request<T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || '请求失败' };
    }
    
    return { success: true, data };
  } catch (error) {
    return { success: false, error: '网络错误' };
  }
}

// 认证API
export const authApi = {
  login: (form: LoginForm) => 
    request<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(form),
    }),
  
  register: (form: RegisterForm) => 
    request<{ user: User; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(form),
    }),
  
  getMe: () => 
    request<User>('/auth/me'),
  
  logout: () => {
    localStorage.removeItem('token');
    return Promise.resolve({ success: true });
  },
};

// 项目API
export const projectsApi = {
  getAll: () => 
    request<Project[]>('/projects'),
  
  getById: (id: string) => 
    request<Project>(`/projects/${id}`),
  
  create: (data: Partial<Project>) => 
    request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<Project>) => 
    request<Project>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) => 
    request<void>(`/projects/${id}`, {
      method: 'DELETE',
    }),
};

// 里程碑API
export const milestonesApi = {
  getByProject: (projectId: string) => 
    request<Milestone[]>(`/milestones/project/${projectId}`),
  
  create: (data: Partial<Milestone>) => 
    request<Milestone>('/milestones', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<Milestone>) => 
    request<Milestone>(`/milestones/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) => 
    request<void>(`/milestones/${id}`, {
      method: 'DELETE',
    }),
};

// 任务API
export const tasksApi = {
  getAll: (params?: { projectId?: string; milestoneId?: string; status?: string }) => {
    const query = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return request<Task[]>(`/tasks${query}`);
  },
  
  getById: (id: string) => 
    request<Task>(`/tasks/${id}`),
  
  create: (data: Partial<Task>) => 
    request<Task>('/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<Task>) => 
    request<Task>(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) => 
    request<void>(`/tasks/${id}`, {
      method: 'DELETE',
    }),
  
  restore: (id: string) => 
    request<void>(`/tasks/${id}/restore`, {
      method: 'POST',
    }),
};

// 附件API
export const attachmentsApi = {
  getByTask: (taskId: string) => 
    request<Attachment[]>(`/attachments/task/${taskId}`),
  
  upload: (taskId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return request<Attachment>(`/attachments/task/${taskId}`, {
      method: 'POST',
      body: formData,
      headers: {}, // 让浏览器自动设置Content-Type
    });
  },
  
  delete: (id: string) => 
    request<void>(`/attachments/${id}`, {
      method: 'DELETE',
    }),
};

// 思维导图API
export const mindMapApi = {
  getByProject: (projectId: string) => 
    request<MindMapNode[]>(`/mindmap/project/${projectId}`),
  
  create: (data: Partial<MindMapNode>) => 
    request<MindMapNode>('/mindmap', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<MindMapNode>) => 
    request<MindMapNode>(`/mindmap/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  updatePositions: (positions: { id: string; x: number; y: number }[]) => 
    request<void>('/mindmap/batch/position', {
      method: 'PUT',
      body: JSON.stringify({ positions }),
    }),
  
  delete: (id: string) => 
    request<void>(`/mindmap/${id}`, {
      method: 'DELETE',
    }),
};

// 日历API
export const calendarApi = {
  getSubscriptions: () => 
    request<CalendarSubscription[]>('/calendar/subscriptions'),
  
  createSubscription: (data: Partial<CalendarSubscription>) => 
    request<CalendarSubscription>('/calendar/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  deleteSubscription: (id: string) => 
    request<void>(`/calendar/subscriptions/${id}`, {
      method: 'DELETE',
    }),
  
  getIcsUrl: (token: string) => `${window.location.origin}/ics/${token}`,
};

// 统计API
export const statsApi = {
  getStats: () => 
    request<Stats>('/stats'),
};

// 回收站API
export const trashApi = {
  getDeletedTasks: () => 
    request<Task[]>('/trash'),
  
  restoreTask: (id: string) => 
    request<void>(`/trash/${id}/restore`, {
      method: 'POST',
    }),
  
  permanentDelete: (id: string) => 
    request<void>(`/trash/${id}`, {
      method: 'DELETE',
    }),
  
  empty: () => 
    request<void>('/trash/empty', {
      method: 'POST',
    }),
};
